export type AgingBucket = '0-30' | '31-60' | '61-90' | '90+';

export interface AgingInvoice {
  id: string;
  customer: string;
  invoiceNumber: string;
  amount: number;
  issuedDate: string;
  terms: string;
  agingDays: number;
  status: 'open' | 'partial' | 'disputed';
}

export const agingBucketOf = (days: number): AgingBucket => {
  if (days <= 30) return '0-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  return '90+';
};

export const totalByBucket = (invoices: readonly AgingInvoice[]): Record<AgingBucket, number> => {
  const totals: Record<AgingBucket, number> = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
  for (const inv of invoices) {
    totals[agingBucketOf(inv.agingDays)] += inv.amount;
  }
  return totals;
};

const mk = (
  id: string,
  customer: string,
  invoiceNumber: string,
  amount: number,
  agingDays: number,
  terms: string = 'Net 30',
  status: AgingInvoice['status'] = 'open',
): AgingInvoice => ({
  id,
  customer,
  invoiceNumber,
  amount,
  issuedDate: `${agingDays}d ago`,
  terms,
  agingDays,
  status,
});

export const SEED_INVOICES: readonly AgingInvoice[] = [
  // Acme Corp — anchors Beat #1: $42,000 total in 90+ bucket
  mk('inv-acme-001', 'Acme Corp', 'INV-1001', 18000, 127),
  mk('inv-acme-002', 'Acme Corp', 'INV-1014', 12000, 104),
  mk('inv-acme-003', 'Acme Corp', 'INV-1022', 8000, 96),
  mk('inv-acme-004', 'Acme Corp', 'INV-1031', 4000, 92),
  mk('inv-acme-005', 'Acme Corp', 'INV-1049', 6200, 58),
  mk('inv-acme-006', 'Acme Corp', 'INV-1061', 3450, 42),
  mk('inv-acme-007', 'Acme Corp', 'INV-1079', 9800, 18, '2/10 Net 30'),

  // Peak Industries
  mk('inv-peak-001', 'Peak Industries', 'INV-2003', 5200, 73),
  mk('inv-peak-002', 'Peak Industries', 'INV-2011', 7500, 64),
  mk('inv-peak-003', 'Peak Industries', 'INV-2027', 4100, 48),
  mk('inv-peak-004', 'Peak Industries', 'INV-2034', 8900, 36),
  mk('inv-peak-005', 'Peak Industries', 'INV-2055', 6300, 22),
  mk('inv-peak-006', 'Peak Industries', 'INV-2068', 3700, 11),

  // Delta Retail — Beat #7 write-off candidate ($800 small line)
  mk('inv-delta-001', 'Delta Retail', 'INV-3002', 800, 118),
  mk('inv-delta-002', 'Delta Retail', 'INV-3015', 4200, 71),
  mk('inv-delta-003', 'Delta Retail', 'INV-3029', 6800, 44),
  mk('inv-delta-004', 'Delta Retail', 'INV-3041', 9200, 26),
  mk('inv-delta-005', 'Delta Retail', 'INV-3056', 3100, 14),

  // TechStart — Beat #4 short-pay candidate ($5,000 invoice)
  mk('inv-techstart-001', 'TechStart', 'INV-4007', 5000, 8),
  mk('inv-techstart-002', 'TechStart', 'INV-4012', 3200, 33),
  mk('inv-techstart-003', 'TechStart', 'INV-4020', 7400, 62),
  mk('inv-techstart-004', 'TechStart', 'INV-4028', 2900, 84),

  // Coresystems
  mk('inv-core-001', 'Core Systems', 'INV-5003', 4400, 89, 'Net 30', 'disputed'),
  mk('inv-core-002', 'Core Systems', 'INV-5016', 3300, 55),
  mk('inv-core-003', 'Core Systems', 'INV-5028', 6100, 29),
  mk('inv-core-004', 'Core Systems', 'INV-5039', 5700, 12),

  // Bright Ventures
  mk('inv-bright-001', 'Bright Ventures', 'INV-6001', 1200, 97),
  mk('inv-bright-002', 'Bright Ventures', 'INV-6012', 3600, 68),
  mk('inv-bright-003', 'Bright Ventures', 'INV-6024', 2800, 45),
  mk('inv-bright-004', 'Bright Ventures', 'INV-6037', 4500, 19),

  // Atlas Trading
  mk('inv-atlas-001', 'Atlas Trading', 'INV-7002', 8700, 109),
  mk('inv-atlas-002', 'Atlas Trading', 'INV-7014', 5500, 77),
  mk('inv-atlas-003', 'Atlas Trading', 'INV-7025', 6200, 51),
  mk('inv-atlas-004', 'Atlas Trading', 'INV-7036', 3800, 24),
  mk('inv-atlas-005', 'Atlas Trading', 'INV-7049', 2400, 6),

  // Bluebird Foods
  mk('inv-blue-001', 'Bluebird Foods', 'INV-8004', 5100, 82),
  mk('inv-blue-002', 'Bluebird Foods', 'INV-8016', 4300, 51),
  mk('inv-blue-003', 'Bluebird Foods', 'INV-8028', 3900, 27),
  mk('inv-blue-004', 'Bluebird Foods', 'INV-8039', 2100, 9),

  // Cypress Logistics
  mk('inv-cyp-001', 'Cypress Logistics', 'INV-9003', 6500, 94),
  mk('inv-cyp-002', 'Cypress Logistics', 'INV-9014', 4700, 63),
  mk('inv-cyp-003', 'Cypress Logistics', 'INV-9026', 5900, 38),
  mk('inv-cyp-004', 'Cypress Logistics', 'INV-9038', 3400, 17),

  // Downtown Grocers
  mk('inv-down-001', 'Downtown Grocers', 'INV-10002', 2600, 72),
  mk('inv-down-002', 'Downtown Grocers', 'INV-10013', 4100, 47),
  mk('inv-down-003', 'Downtown Grocers', 'INV-10025', 3300, 21),
  mk('inv-down-004', 'Downtown Grocers', 'INV-10036', 1800, 4),

  // Emerald Imports
  mk('inv-em-001', 'Emerald Imports', 'INV-11001', 7100, 99),
  mk('inv-em-002', 'Emerald Imports', 'INV-11013', 5400, 66),
  mk('inv-em-003', 'Emerald Imports', 'INV-11025', 4800, 41),
  mk('inv-em-004', 'Emerald Imports', 'INV-11037', 2900, 15),

  // Forest Wholesale
  mk('inv-for-001', 'Forest Wholesale', 'INV-12002', 3800, 85),
  mk('inv-for-002', 'Forest Wholesale', 'INV-12014', 6300, 59),
  mk('inv-for-003', 'Forest Wholesale', 'INV-12026', 4200, 32),
  mk('inv-for-004', 'Forest Wholesale', 'INV-12038', 3500, 10),

  // Granite Partners
  mk('inv-gran-001', 'Granite Partners', 'INV-13003', 9400, 106),
  mk('inv-gran-002', 'Granite Partners', 'INV-13015', 6800, 79),
  mk('inv-gran-003', 'Granite Partners', 'INV-13027', 5200, 46),
  mk('inv-gran-004', 'Granite Partners', 'INV-13039', 3100, 23),

  // Harbor Supply
  mk('inv-harb-001', 'Harbor Supply', 'INV-14004', 4600, 75),
  mk('inv-harb-002', 'Harbor Supply', 'INV-14016', 5800, 53),
  mk('inv-harb-003', 'Harbor Supply', 'INV-14028', 3700, 30),
  mk('inv-harb-004', 'Harbor Supply', 'INV-14040', 2200, 7),

  // Ivory Market
  mk('inv-iv-001', 'Ivory Market', 'INV-15002', 5100, 91),
  mk('inv-iv-002', 'Ivory Market', 'INV-15013', 4400, 67),
  mk('inv-iv-003', 'Ivory Market', 'INV-15025', 3900, 40),
  mk('inv-iv-004', 'Ivory Market', 'INV-15037', 2800, 13),

  // Juniper Restaurants
  mk('inv-jun-001', 'Juniper Restaurants', 'INV-16003', 3200, 88),
  mk('inv-jun-002', 'Juniper Restaurants', 'INV-16015', 4800, 54),
  mk('inv-jun-003', 'Juniper Restaurants', 'INV-16027', 5500, 28),
  mk('inv-jun-004', 'Juniper Restaurants', 'INV-16039', 3600, 16),

  // Kite Manufacturing
  mk('inv-kite-001', 'Kite Manufacturing', 'INV-17001', 7600, 102),
  mk('inv-kite-002', 'Kite Manufacturing', 'INV-17013', 6200, 70),
  mk('inv-kite-003', 'Kite Manufacturing', 'INV-17025', 4500, 37),
  mk('inv-kite-004', 'Kite Manufacturing', 'INV-17037', 3000, 5),

  // Extra 0-30 activity to hit 80 rows and round out the near-term bucket
  mk('inv-peak-007', 'Peak Industries', 'INV-2072', 2400, 3),
  mk('inv-atlas-006', 'Atlas Trading', 'INV-7057', 1900, 2),
  mk('inv-bright-005', 'Bright Ventures', 'INV-6049', 2100, 1),
  mk('inv-core-005', 'Core Systems', 'INV-5051', 3800, 8),
  mk('inv-delta-006', 'Delta Retail', 'INV-3068', 1600, 3),
];
